import { useMemo } from "react";

import {
  SteppedMultiColumnDropdown,
  type SteppedMultiColumnDropdownConfig,
  SelectField,
} from "@stacklet/ui";

/** Props for JiraSpace component */
interface Props {
  /** Available spaces from the selected Jira instance */
  spaces: {
    key: string;
    name: string;
  }[];
  /** Default space for the selected instance (if configured) */
  defaultSpace?: {
    key: string;
    name: string;
  } | null;
  /** Currently selected space key (if any) */
  selectedSpaceKey?: string;
  /** Callback when space selection changes */
  onChange: (selections: string[]) => void;
  /** Whether the component is disabled (e.g., no instance selected) */
  disabled?: boolean;
}

export function JiraSpace({
  spaces,
  defaultSpace,
  selectedSpaceKey,
  onChange,
  disabled = false,
}: Props) {
  // Transform spaces to dropdown items, marking the default space
  const spaceItems = useMemo(
    () =>
      spaces.map((space) => ({
        id: space.key,
        name:
          space.key === defaultSpace?.key
            ? `${space.name} (Default)`
            : space.name,
      })),
    [spaces, defaultSpace],
  );

  // Simple single column configuration
  const config: SteppedMultiColumnDropdownConfig = useMemo(
    () => [
      [
        {
          type: "list",
          title: "Select Jira Space",
          items: spaceItems,
          flex: true,
        },
      ],
    ],
    [spaceItems],
  );

  // Display selected value or placeholder in trigger
  const triggerText = useMemo(() => {
    if (disabled) {
      return "Jira Instance Required";
    }
    if (spaceItems.length === 0) {
      return "No Spaces Available";
    }
    if (selectedSpaceKey) {
      const selectedSpace = spaceItems.find(
        (item) => item.id === selectedSpaceKey,
      );
      return selectedSpace?.name || "Select Space";
    }
    return "Select Space";
  }, [disabled, spaceItems, selectedSpaceKey]);

  const trigger = ({ isOpen }: { isOpen: boolean }) => (
    <SelectField
      aria-label="Select Jira Space"
      className="w-[350px]"
      isDisabled={disabled}
      isOpen={isOpen}
      value={triggerText}
    />
  );

  return (
    <SteppedMultiColumnDropdown
      config={config}
      initialSelections={selectedSpaceKey ? [selectedSpaceKey] : undefined}
      onChange={onChange}
      trigger={trigger}
      autoHeight
    />
  );
}
